import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUserData } from "../../features/store";
import { useNavigate } from "react-router-dom";
import DataTable from "./AccountTable";
import { GridActionsCellItem, useGridApiRef } from "@mui/x-data-grid";
import { FaRegCirclePlay } from "react-icons/fa6";
import { getAccount } from "../../apis/Auth.Api";

import { FaEdit, FaUnlockAlt, FaTrashAlt } from "react-icons/fa";

import DeleteAccountPopUp from "./DeleteAccountPopUp";
import ResetAccountPopUp from "./ResetAccountPopUp";
import EditAccountPopUp from "./EditAccountPopUp";
import CreateAccountPopUp from "./CreateAccountPopUp";
import InspectAccountPopUp from "./InspectAccountPopUp";
import { TopNavBar } from "../../components/TopNavBar";
import { Card, RectBtn } from "../../components/General";
import { IoIosAddCircleOutline } from "react-icons/io";
import { PERMISSION } from "../../configs/global";
import { useSnackbar } from "notistack";
import { getAuthorizedRoomsByAccount } from "../../apis/Room.Api";

const ManageAccounts = () => {
    // ========== Hooks ===========
    const userData = useSelector(selectUserData);
    const navigate = useNavigate();
    const apiRef = useGridApiRef();
    const { enqueueSnackbar } = useSnackbar();
    // info to display
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});
    const [selectedUserRooms, setSelectedUserRooms] = useState(null);

    // pop-up switches
    const [openAdd, setOpenAdd] = useState(false);
    const [openInspect, setOpenInspect] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openReset, setOpenReset] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    // for update
    const [updateName, setUpdateName] = useState("");
    const [updatePermission, setUpdatePermission] = useState("");

    // for inpsect
    const [canDelete, setCanDelete] = useState(false);

    // ============= UseEffect =====================
    // Init: check for user permission & get all accounts
    useEffect(() => {
        if (userData.permission !== PERMISSION.SUPER && userData.permission !== PERMISSION.ADMIN) {
            alert("Access denied. Please contact supervisor for permission.");
            navigate("/dashboard");
            return;
        }
        getUsers();
    }, []);

    // UseEffect Helpers
    async function getUsers() {
        let u = [];
        const response = await getAccount();

        if (!response.data.indicator) {
            enqueueSnackbar("Failed to fetch accounts. Please try again later.", {
                variant: "error",
            });
            return;
        }

        let userList = response.data.message;
        for (let user of userList) {
            // parse response to row object

            let obj = {
                id: user._id,
                userId: user.user_id,
                name: user.user_name,
                reset: user.reset,
                permission: user.permission,
            };
            u.push(obj);
        }
        setUsers(u);
    }

    // ================= Variables ===================
    // columns:
    // userId | name | reset | permission
    const columns = [
        // {field: "id", headerName: "ID", width: 70},
        { field: "userId", headerName: "User ID", flex: 2 },
        { field: "name", headerName: "Name", flex: 2 },
        {
            field: "reset",
            headerName: "Reset",
            flex: 2,
        },
        {
            field: "permission",
            headerName: "Permission Level",
            flex: 2,
        },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<FaRegCirclePlay />}
                    label="Actions"
                    showInMenu
                    onClick={() => inspectAccount(params)}
                    disabled={params.row.permission === 5}
                />,
                <GridActionsCellItem
                    icon={<FaEdit />}
                    label="Edit"
                    showInMenu
                    onClick={() => {
                        attemptEditAccount(params);
                    }}
                    disabled={params.row.permission === 5}
                />,
                <GridActionsCellItem
                    icon={<FaUnlockAlt />}
                    label="Reset"
                    showInMenu
                    onClick={() => {
                        attemptResetAccount(params);
                    }}
                    disabled={params.row.reset || params.row.permission === 5}
                />,
                <GridActionsCellItem
                    icon={<FaTrashAlt color="#e5383b" />}
                    label="Delete"
                    showInMenu
                    sx={{ color: "#e5383b" }}
                    onClick={() => {
                        attemptDeleteAccount(params);
                    }}
                    disabled={params.row.userId === userData.userId || params.row.permission === 5}
                />,
            ],
        },
    ];

    // ============== Actions on accounts/ authorization =================
    // create
    function attemptAddAccount() {
        setOpenAdd(true);
    }

    // inspect
    function inspectAccount(params) {
        setSelectedUser(params.row);
        getUserRooms(params.row);
        setCanDelete(false);
        setOpenInspect(true);
    }
    async function getUserRooms(user) {
        const userId = user.userId;

        const response = await getAuthorizedRoomsByAccount({ user_id: userId });
        if (!response.data.indicator) {
            enqueueSnackbar(response.data.message, { variant: "error" });
            return;
        }
        const reformatRoomDict = processAuthRoomResponseMessage(response.data.message);
        setSelectedUserRooms(reformatRoomDict);
    }

    /**
     * @param message format: [room0, room1, ...]
     * room = {
     * smart_toilet.room._id: int
     * smart_toilet.room.building_id: str
     * smart_toilet.room.floor: str
     * smart_toilet.room.room_id: str
     * smart_toilet.room.room_name: str
     * }
     * @return reformat = { roomId  : {...}, roomId2 : {...} ...}
     * OR null if no room is found
     */
    function processAuthRoomResponseMessage(message) {
        let arr = [];
        let dict = {};
        if (message.length === 0) return {};
        for (const room of message) {
            let reformat = {
                id: room["_id"],
                buildingId: room["building_id"],
                floor: room["floor"],
                roomId: room["room_id"],
                roomName: room["room_name"],
                hidden: false,
            };
            dict[reformat.roomId] = reformat;
            arr.push(reformat);
        }
        return dict;
    }

    // edit
    function attemptEditAccount(params) {
        setSelectedUser(params.row);
        setUpdateName(params.row.name);

        setUpdatePermission(params.row.permission);
        setOpenEdit(true);
    }
    // reset
    function attemptResetAccount(params) {
        setSelectedUser(params.row);
        setOpenReset(true);
    }

    // delete
    function attemptDeleteAccount(params) {
        setSelectedUser(params.row);
        setOpenDelete(true);
    }

    return (
        <div className="manage-accounts">
            <TopNavBar hideDropDown={true}></TopNavBar>
            <div className="manage-accounts-content">
                <div className="manage-accounts-header-row">
                    <h3> Manage Accounts</h3>
                    <div className="btn-wrapper">
                        <RectBtn onClick={attemptAddAccount}>
                            <div className="btn-row">
                                <IoIosAddCircleOutline className="icon" />
                                <p>Add Account</p>
                            </div>
                        </RectBtn>
                    </div>
                </div>
                <Card>
                    <DataTable rows={users} columns={columns} apiRef={apiRef} />
                </Card>
            </div>
            <div className="pop-ups">
                <CreateAccountPopUp openAdd={openAdd} setOpenAdd={setOpenAdd} users={users} setUsers={setUsers} />
                <InspectAccountPopUp
                    selectedUser={selectedUser}
                    selectedUserRooms={selectedUserRooms}
                    setSelectedUserRooms={setSelectedUserRooms}
                    openInspect={openInspect}
                    setOpenInspect={setOpenInspect}
                    canDelete={canDelete}
                    setCanDelete={setCanDelete}
                />
                <EditAccountPopUp
                    openEdit={openEdit}
                    setOpenEdit={setOpenEdit}
                    updateName={updateName}
                    setUpdateName={setUpdateName}
                    updatePermission={updatePermission}
                    setUpdatePermission={setUpdatePermission}
                    selectedUser={selectedUser}
                    apiRef={apiRef}
                    users={users}
                />
                <ResetAccountPopUp
                    openReset={openReset}
                    setOpenReset={setOpenReset}
                    selectedUser={selectedUser}
                    apiRef={apiRef}
                />
                <DeleteAccountPopUp
                    openDelete={openDelete}
                    setOpenDelete={setOpenDelete}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                    apiRef={apiRef}
                />
            </div>
        </div>
    );
};

export default ManageAccounts;
